/**
 * Global cache manager for performance optimizations
 * Prevents memory leaks from growing caches during long sessions
 */

interface CacheEntry {
  timestamp: number;
  [key: string]: any;
}

interface CacheConfig {
  maxAge: number;
  maxSize: number;
}

class CacheManager {
  private caches: Map<string, { cache: Map<string, CacheEntry>, config: CacheConfig }> = new Map();
  private cleanupInterval: NodeJS.Timeout | null = null;
  
  constructor() {
    // Run cleanup every 5 minutes
    this.cleanupInterval = setInterval(() => this.performCleanup(), 5 * 60 * 1000);
  }

  registerCache(name: string, cache: Map<string, CacheEntry>, config: CacheConfig) {
    this.caches.set(name, { cache, config });
  }

  private performCleanup() {
    const now = Date.now();
    let totalCleared = 0;

    for (const [name, { cache, config }] of this.caches) {
      const initialSize = cache.size;
      
      // Remove expired entries
      for (const [key, entry] of cache) {
        if (now - entry.timestamp > config.maxAge) {
          cache.delete(key);
          totalCleared++;
        }
      }

      // If still too large, remove oldest entries
      if (cache.size > config.maxSize) {
        const entries = Array.from(cache.entries()).sort((a, b) => a[1].timestamp - b[1].timestamp);
        const toRemove = entries.slice(0, cache.size - config.maxSize);
        
        for (const [key] of toRemove) {
          cache.delete(key);
          totalCleared++;
        }
      }

      if (cache.size < initialSize) {
        console.log(`[CacheManager] Cleaned ${initialSize - cache.size} entries from ${name} cache`);
      }
    }

    if (totalCleared > 0) {
      console.log(`[CacheManager] Total cleanup: ${totalCleared} entries removed across all caches`);
    }
  }

  destroy() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.caches.clear();
  }
}

// Global instance
export const globalCacheManager = new CacheManager();

// Helper to register caches with appropriate configs
export function registerPerformanceCache(
  name: string, 
  cache: Map<string, any>, 
  maxAge: number = 5 * 60 * 1000, // 5 minutes default
  maxSize: number = 1000 // 1000 entries default
) {
  globalCacheManager.registerCache(name, cache as Map<string, CacheEntry>, { maxAge, maxSize });
}

// Cleanup function for manual use
export function performManualCleanup() {
  (globalCacheManager as any).performCleanup();
}
